/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { cva, VariantProps } from 'class-variance-authority';
import { uniqueId } from 'lodash-es';

const radioButtonVariants = cva(
  'z-10 flex min-w-48 flex-1 cursor-pointer items-center justify-center gap-3 rounded-md border border-ozggray-300 bg-background-100 px-6 py-4 text-center text-base font-normal shadow-xs hover:bg-background-150 hover:shadow-lg peer-checked:scale-[1.03]   peer-checked:font-medium peer-checked:shadow-lg peer-focus-visible:outline-solid peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
  {
    variants: {
      variant: {
        default: 'text-primary peer-checked:border-primary peer-focus-visible:outline-focus',
        accepted: 'text-text peer-checked:border-traffic-light-green peer-focus-visible:outline-traffic-light-green',
        declined: 'text-text peer-checked:border-traffic-light-red peer-focus-visible:outline-traffic-light-red',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);
export type RadioButtonVariants = VariantProps<typeof radioButtonVariants>;

const radioButtonTextVariants = cva('h-fit', {
  variants: {
    width: {
      default: '',
      full: 'w-full',
    },
  },
  defaultVariants: {
    width: 'default',
  },
});
export type RadioButtonTextVariants = VariantProps<typeof radioButtonTextVariants>;

@Component({
  selector: 'ods-radio-button-card',
  imports: [CommonModule, ReactiveFormsModule],
  host: { class: 'relative flex' },
  template: `<input
      type="radio"
      class="peer absolute top-0 left-0 z-0 h-0 w-0 opacity-0"
      [id]="radioButtonId"
      name="{{ name }}"
      attr.name="{{ name }}"
      [formControl]="fieldControl"
      value="{{ value }}"
    />
    <label [for]="radioButtonId" [ngClass]="radioButtonVariants({ variant })">
      <ng-content />
      <p [ngClass]="radioButtonTextVariants({ width: textWidth })">{{ label }}</p>
    </label>`,
})
export class RadioButtonCardComponent {
  @Input({ required: true }) label: string;
  @Input({ required: true }) name: string;
  @Input({ required: true }) value: string;
  @Input() fieldControl: FormControl = new FormControl();
  @Input() variant: RadioButtonVariants['variant'];
  @Input() textWidth: RadioButtonTextVariants['width'];

  public readonly radioButtonVariants = radioButtonVariants;
  public readonly radioButtonTextVariants = radioButtonTextVariants;
  public readonly radioButtonId: string = uniqueId('radio-button-card-');
}
